import { ArrowPathIcon, InformationCircleIcon, TrashIcon } from "@heroicons/react/24/outline"
import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { useMutation } from "urql"

import { Button } from "../../../components/shared/Buttons"
import HealMeTooltip from "../../../components/shared/HealMeTooltip"
import { useToast } from "../../../contexts/ToastContext"
import CalIcon from "../ProCalendarSettings/CalIcon"

import { REFRESH_APPLE_CALENDAR_MIRROR_MUTATION, START_CALENDAR_MIGRATION_MUTATION } from "./graphql"
import HealthReauthBanner from "./HealthReauthBanner"
import MigrationProgressPanel from "./MigrationProgressPanel"
import CompleteReconnectMigrationModal from "./CompleteReconnectMigrationModal"
import EmptyConflictBanner from "./EmptyConflictBanner"
import ReconnectLegacyBanner from "./ReconnectLegacyBanner"
import ResyncConfirmModal from "./ResyncConfirmModal"
import {
  CONFLICT_SELECTION_TOOLTIP,
  WRITE_SELECTION_EMPTY_LABEL,
  WRITE_SELECTION_TOOLTIP,
  connectionHealth,
  conflictSelectableCalendars,
  implementationLabel,
  lastSyncLabel,
  migrationsForConnection,
  providerLabel,
  supportsWriteSelection,
  syncActivityMessage
} from "./selectors"
import {
  CalendarConnectionSummary,
  CalendarMigrationStatus,
  CalendarProvider,
  RefreshAppleCalendarMirrorPayload,
  StartMigrationPayload
} from "./types"

interface Props {
  connection: CalendarConnectionSummary
  migrations: CalendarMigrationStatus[]
  connectableProviders: CalendarProvider[]
  onEditConflicts: (connection: CalendarConnectionSummary) => void
  onEditWrites: (connection: CalendarConnectionSummary) => void
  onDisconnect: (connection: CalendarConnectionSummary) => void
  onChanged: () => void
}

const HEALTH_CLASSES = {
  ok: "bg-teal/10 text-teal",
  pending: "bg-gray-ultralight text-gray-dark",
  warning: "bg-yellow/20 text-black",
  error: "bg-red/10 text-red"
}

const calIconProvider = (provider: string) => (provider === "microsoft" ? "live_connect" : provider)

const RESYNC_POLL_MS = 2000
const RESYNC_POLL_MAX_MS = 60_000
const APPLE_MIRROR_REFRESH_COOLDOWN_MS = 30_000

// One card per calendar account (connection). Shows provider, native/legacy
// badge, health, last sync, and migration state. Technical identifiers
// (account UID, provider calendar IDs, tokens, feed URLs) never render here.
const AccountCard = ({
  connection,
  migrations,
  connectableProviders,
  onEditConflicts,
  onEditWrites,
  onDisconnect,
  onChanged
}: Props) => {
  const [startingMigration, setStartingMigration] = useState(false)
  const [showResyncModal, setShowResyncModal] = useState(false)
  const [showCompleteReconnectModal, setShowCompleteReconnectModal] = useState(false)
  const [resyncQueued, setResyncQueued] = useState(false)
  const [lastResyncSummary, setLastResyncSummary] = useState<string | null>(null)
  const [mirrorRefreshQueued, setMirrorRefreshQueued] = useState(false)
  const [mirrorRefreshCoolingDown, setMirrorRefreshCoolingDown] = useState(false)
  const pollStartedAt = useRef<number | null>(null)
  const mirrorRefreshCooldownTimer = useRef<number | null>(null)
  const [, startMigration] = useMutation<{ startCalendarMigration: StartMigrationPayload }>(
    START_CALENDAR_MIGRATION_MUTATION
  )
  const [, refreshAppleMirror] = useMutation<{ refreshAppleCalendarMirror: RefreshAppleCalendarMirrorPayload }>(
    REFRESH_APPLE_CALENDAR_MIRROR_MUTATION
  )
  const { showToast } = useToast()

  const health = connectionHealth(connection)
  const connectionMigrations = migrationsForConnection(migrations, connection.publicId).filter(
    (migration) => !["completed", "rollback"].includes(migration.state)
  )
  // A linked legacy connection with no running migration can start the event
  // transfer. Linkage comes from the signed OAuth flow only.
  const canStartMigration =
    connection.implementation === "cronofy" &&
    Boolean(connection.linkedConnectionPublicId) &&
    connectionMigrations.length === 0

  const activityMessage = syncActivityMessage(connection.syncActivity, { resyncQueued })
  const showResync = connection.canResyncBookings
  const showCompleteReconnect = connection.canCompleteReconnectMigration
  const resyncDisabled = resyncQueued || Boolean(connection.syncActivity)
  const showMirrorRefresh = connection.canRefreshMirror
  const mirrorRefreshDisabled =
    mirrorRefreshQueued ||
    mirrorRefreshCoolingDown ||
    connection.mirrorRefreshRateLimited ||
    Boolean(connection.syncActivity)

  useEffect(() => {
    return () => {
      if (mirrorRefreshCooldownTimer.current != null) {
        window.clearTimeout(mirrorRefreshCooldownTimer.current)
      }
    }
  }, [])

  useEffect(() => {
    const shouldPoll = resyncQueued || Boolean(connection.syncActivity)
    if (!shouldPoll) {
      pollStartedAt.current = null
      return undefined
    }

    if (pollStartedAt.current == null) pollStartedAt.current = Date.now()

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - (pollStartedAt.current || Date.now())
      if (elapsed > RESYNC_POLL_MAX_MS) {
        setResyncQueued(false)
        pollStartedAt.current = null
        return
      }
      onChanged()
    }, RESYNC_POLL_MS)

    return () => window.clearInterval(timer)
  }, [resyncQueued, connection.syncActivity, onChanged])

  useEffect(() => {
    if (resyncQueued && !connection.syncActivity) {
      // Activity cleared after enqueue — stop the latch once the first idle
      // refetch lands (counts already toasted on mutation success).
      setResyncQueued(false)
    }
  }, [resyncQueued, connection.syncActivity])

  const beginMirrorRefresh = () => {
    if (mirrorRefreshDisabled) return
    setMirrorRefreshQueued(true)
    refreshAppleMirror({ connectionPublicId: connection.publicId }).then((result) => {
      const payload = result.data?.refreshAppleCalendarMirror
      if (payload?.result === "success") {
        showToast("Refreshing from Apple…")
        setMirrorRefreshCoolingDown(true)
        if (mirrorRefreshCooldownTimer.current != null) {
          window.clearTimeout(mirrorRefreshCooldownTimer.current)
        }
        mirrorRefreshCooldownTimer.current = window.setTimeout(() => {
          setMirrorRefreshCoolingDown(false)
          mirrorRefreshCooldownTimer.current = null
        }, APPLE_MIRROR_REFRESH_COOLDOWN_MS)
        onChanged()
      } else {
        showToast({
          content: payload?.errors?.[0] || "Could not refresh Apple Calendar",
          type: "error"
        })
      }
      setMirrorRefreshQueued(false)
    })
  }

  const beginMigration = () => {
    setStartingMigration(true)
    startMigration({ connectionPublicId: connection.publicId, dryRun: false }).then((result) => {
      setStartingMigration(false)
      const payload = result.data?.startCalendarMigration
      if (payload?.result === "success") {
        showToast("Calendar transfer started")
        onChanged()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not start the calendar transfer", type: "error" })
      }
    })
  }

  const conflictNames = conflictSelectableCalendars(connection)
    .filter((calendar) => calendar.selectedForConflicts)
    .map((calendar) => calendar.displayName || "Calendar")
  const writeNames = connection.calendars
    .filter((calendar) => calendar.selectedForWrite)
    .map((calendar) => calendar.displayName || "Calendar")

  return (
    <div className="rounded-xl border border-gray-light p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <CalIcon
            provider={calIconProvider(connection.provider)}
            className="size-10 flex-none border-none"
            iconClassName="h-6 w-6"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold">{providerLabel(connection.provider)}</h3>
              <span
                className={twMerge(
                  "rounded px-2 py-0.5 text-xs font-semibold",
                  connection.implementation === "native" ? "bg-teal/10 text-teal" : "bg-gray-ultralight text-gray-dark"
                )}>
                {implementationLabel(connection.implementation)}
              </span>
              <span className={twMerge("rounded px-2 py-0.5 text-xs font-semibold", HEALTH_CLASSES[health.tone])}>
                {health.label}
              </span>
            </div>
            {connection.displayIdentity && (
              <div className="truncate text-sm text-gray-dark">{connection.displayIdentity}</div>
            )}
            <div className="text-xs text-gray-dark">{lastSyncLabel(connection.lastSyncedAt)}</div>
          </div>
        </div>
        <div className="flex flex-none items-center gap-1">
          {showResync && (
            <button
              type="button"
              aria-label="Resync bookings to Google"
              title="Resync upcoming Heal.me bookings to Google"
              disabled={resyncDisabled}
              className={twMerge(
                "flex size-8 items-center justify-center rounded text-teal hover:bg-teal/10 focus:outline-none focus:ring-2 focus:ring-blue active:bg-teal/20",
                resyncDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent active:bg-transparent"
              )}
              onClick={() => setShowResyncModal(true)}>
              {resyncQueued || connection.syncActivity ? (
                <span
                  className="inline-block size-4 animate-spin rounded-full border-2 border-teal border-t-transparent"
                  aria-hidden
                />
              ) : (
                <ArrowPathIcon className="size-5" />
              )}
            </button>
          )}
          {showMirrorRefresh && (
            <button
              type="button"
              aria-label="Refresh from Apple Calendar"
              title={
                mirrorRefreshCoolingDown || connection.mirrorRefreshRateLimited
                  ? "Refresh available again in a few seconds"
                  : "Refresh from Apple Calendar"
              }
              disabled={mirrorRefreshDisabled}
              className={twMerge(
                "flex size-8 items-center justify-center rounded text-teal hover:bg-teal/10 focus:outline-none focus:ring-2 focus:ring-blue active:bg-teal/20",
                mirrorRefreshDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent active:bg-transparent"
              )}
              onClick={beginMirrorRefresh}>
              {mirrorRefreshQueued ? (
                <span
                  className="inline-block size-4 animate-spin rounded-full border-2 border-teal border-t-transparent"
                  aria-hidden
                />
              ) : (
                <ArrowPathIcon className="size-5" />
              )}
            </button>
          )}
          <button
            type="button"
            aria-label={`Disconnect ${providerLabel(connection.provider)}`}
            className="flex size-8 items-center justify-center rounded text-red hover:bg-red/10 focus:outline-none focus:ring-2 focus:ring-blue active:bg-red/20"
            onClick={() => onDisconnect(connection)}>
            <TrashIcon className="size-5" />
          </button>
        </div>
      </div>

      <ResyncConfirmModal
        connection={showResyncModal ? connection : null}
        onClose={() => setShowResyncModal(false)}
        onResynced={(summary) => {
          setShowResyncModal(false)
          setLastResyncSummary(summary)
          setResyncQueued(true)
          onChanged()
        }}
      />

      <CompleteReconnectMigrationModal
        connection={showCompleteReconnectModal ? connection : null}
        onClose={() => setShowCompleteReconnectModal(false)}
        onCompleted={() => {
          setShowCompleteReconnectModal(false)
          onChanged()
        }}
      />

      {activityMessage && (
        <div
          className="mt-3 flex items-center gap-2 rounded-lg border border-teal/40 bg-teal/5 px-3 py-2 text-sm text-teal"
          role="status"
          aria-live="polite">
          <span
            className="inline-block size-3.5 flex-none animate-spin rounded-full border-2 border-teal border-t-transparent"
            aria-hidden
          />
          <span>{activityMessage}</span>
        </div>
      )}

      <HealthReauthBanner connection={connection} connectableProviders={connectableProviders} />
      <ReconnectLegacyBanner connection={connection} connectableProviders={connectableProviders} />
      <EmptyConflictBanner connection={connection} onEditConflicts={onEditConflicts} />

      {showCompleteReconnect && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-teal bg-teal/5 p-3">
          <div className="min-w-0 flex-1 text-sm">
            Finish switching to Heal.me sync: turn off the old connection, refresh your bookings on Google, and remove
            duplicate booking copies.
          </div>
          <Button
            onClick={() => setShowCompleteReconnectModal(true)}
            type="secondary"
            className=""
            floatingInMobile={false}
            style={{}}>
            Finish cleanup
          </Button>
        </div>
      )}

      {canStartMigration && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-teal bg-teal/5 p-3">
          <div className="min-w-0 flex-1 text-sm">
            This account is linked to your new connection. Start the transfer to move your events to Heal.me&apos;s new
            sync.
          </div>
          <Button
            onClick={beginMigration}
            disabled={startingMigration}
            type="secondary"
            className=""
            floatingInMobile={false}
            style={{}}>
            {startingMigration ? "Starting…" : "Start transfer"}
          </Button>
        </div>
      )}

      {connectionMigrations.map((migration) => (
        <MigrationProgressPanel key={migration.migrationId} migration={migration} onChanged={onChanged} />
      ))}

      {connection.calendars.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            className="flex w-full justify-between rounded-lg border border-gray p-3 text-left text-sm hover:border-teal focus:outline-none focus:ring-2 focus:ring-blue"
            onClick={() => onEditConflicts(connection)}>
            <span className="min-w-0">
              <span className="flex items-center gap-1 text-gray-dark">
                <span>Check for conflicts</span>
                <InformationCircleIcon
                  className="size-4 shrink-0 cursor-help"
                  aria-label="About checking for conflicts"
                  data-tooltip-id={`calendar-conflicts-tooltip-${connection.publicId}`}
                  onClick={(event) => event.stopPropagation()}
                />
              </span>
              <span className="block truncate">
                {conflictNames.length > 0 ? conflictNames.join(", ") : "Not checking this account for conflicts"}
              </span>
            </span>
            <span className="flex-none font-bold text-teal">Edit</span>
          </button>
          <HealMeTooltip
            id={`calendar-conflicts-tooltip-${connection.publicId}`}
            place="top"
            content={CONFLICT_SELECTION_TOOLTIP}
            style={{}}
          />
          {supportsWriteSelection(connection) && (
            <>
              <button
                type="button"
                className="flex w-full justify-between rounded-lg border border-gray p-3 text-left text-sm hover:border-teal focus:outline-none focus:ring-2 focus:ring-blue"
                onClick={() => onEditWrites(connection)}>
                <span className="min-w-0">
                  <span className="flex items-center gap-1 text-gray-dark">
                    <span>Add Heal.me bookings to</span>
                    <InformationCircleIcon
                      className="size-4 shrink-0 cursor-help"
                      aria-label="About adding Heal.me bookings"
                      data-tooltip-id={`calendar-writes-tooltip-${connection.publicId}`}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </span>
                  <span className="block truncate">
                    {writeNames.length > 0 ? writeNames.join(", ") : WRITE_SELECTION_EMPTY_LABEL}
                  </span>
                </span>
                <span className="flex-none font-bold text-teal">Edit</span>
              </button>
              <HealMeTooltip
                id={`calendar-writes-tooltip-${connection.publicId}`}
                place="top"
                content={WRITE_SELECTION_TOOLTIP}
                style={{}}
              />
            </>
          )}
        </div>
      )}

      {lastResyncSummary && !activityMessage && (
        <p className="mt-2 text-xs text-gray-dark">{lastResyncSummary}</p>
      )}

      {connection.implementation === "cronofy" && (
        <p className="mt-3 text-xs text-gray-dark">
          This account still uses our previous sync. Everything on this card keeps working — changes you make here apply
          to it right away.
        </p>
      )}
    </div>
  )
}

export default AccountCard
